import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setActiveRoom, markMessagesRead, createChatRoom } from '../../store/slices/chatSlice';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import socketService from '../../utils/socketService';

const ChatList = () => {
    const { rooms, isLoading } = useSelector((state) => state.chat);
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const handleRoomClick = (room) => {
        dispatch(setActiveRoom(room));
        if (room.unreadCount > 0) {
            dispatch(markMessagesRead(room.id));
            socketService.emit('mark_messages_read', { roomId: room.id });
        }
    };

    if (isLoading && rooms.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (rooms.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6 text-center">
                <UserCircleIcon className="w-16 h-16 mb-2 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No conversations yet.</p>

                {user.role === 'superadmin' ? (
                    <div className="mt-4">
                        <p className="text-xs text-gray-400 mb-3">To start a chat, visit the Users page and click the message icon.</p>
                        <a
                            href="/users"
                            className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Go to Users
                        </a>
                    </div>
                ) : (
                    <button
                        onClick={() => {
                            dispatch(createChatRoom({ /* no ID needed for non-admin */ }));
                        }}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        Start Chat with Admin
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto custom-scrollbar">
            {rooms.map((room) => {
                const otherUser = user.role === 'superadmin' ? room.participant : { name: 'Administrator' };

                if (!otherUser) return null; // Safety check

                const isUnread = room.unreadCount > 0;

                return (
                    <div
                        key={room.id}
                        onClick={() => handleRoomClick(room)}
                        className={`p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-gray-100 ${isUnread ? 'bg-blue-50' : ''
                            }`}
                    >
                        <div className="flex items-start gap-3">
                            <div className="relative">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold shrink-0">
                                    {otherUser.name.charAt(0).toUpperCase()}
                                </div>
                                {/* Status indicator (mock) */}
                                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-baseline mb-1">
                                    <h4 className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                                        {otherUser.name}
                                    </h4>
                                    {room.lastMessageAt && (
                                        <span className="text-[10px] text-gray-400 shrink-0">
                                            {new Date(room.lastMessageAt).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                                <p className={`text-xs truncate ${isUnread ? 'font-semibold text-gray-800' : 'text-gray-500'}`}>
                                    {room.lastMessage || 'Attempted to contact you'}
                                </p>
                            </div>

                            {isUnread && (
                                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-[10px] text-white font-bold shrink-0 mt-1">
                                    {room.unreadCount}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default ChatList;
